/* eslint-disable max-depth */
import {
    type NodeCfg,
    type NodeSocketKind,
    type ProcessWorkInstruction,
} from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { getUID } from '@redviking/argonaut-util/src/getUID';
import { gqlClient } from 'src/util/gql-client';
import { GetProcessDataElementsForWorkInstructionInputsDocument, GetWorkInstructionVersionDetailDocument } from 'types/db';
import { ArgoWorkInstructionVersionStatusEnum } from '@redviking/argonaut-util/types/db-enums/argo/work_instruction_version_status';
import { ProcessDataElementCollectionKindEnum, ProcessDataElementKindEnum } from '@redviking/argonaut-util/types/db-enums/process';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import { AsyncComponent } from 'vue';
import { fromZodError } from 'zod-validation-error';
import { Notify } from 'src/notifications';
import { CausalError } from '@redviking/causal-error';
import { workInstructionVersionSchema } from '@redviking/argonaut-util/types/work-instruction-version';
import { accessor } from '@redviking/argonaut-core-ui/src/store';
import type { NodeSpec } from './NodeSpec';
import { defaultControlUpdateValueHandler } from '@redviking/argonaut-core-ui/src/applet/design/flows/controls/control-event-handler';

export const workInstructionUiNodeSpec: NodeSpec<NodeCfg<'workInstruction'>> = {
    nodeCategory: 'argonautApi',
    kindLabel: 'Work Instruction',
    requiredSockets: [ 'activate', 'materialId' ],
    extraComponent: cfg => {
        if (cfg.controlValues.workInstructionDetail.kind === 'process') {
            const comp: AsyncComponent = () => import('@redviking/argonaut-core-ui/src/applet/design/flows/inspector/WorkInstructionNode.helper.vue').then(m => m.default);
            return comp;
        }
        return null;
    },
    controls: nodeCfg => ({
        workInstructionVersionId: defineControlOpts({
            cfg: {
                kind: 'selectWorkInstructionVersion',
                label: 'Work Instruction',
            },
            on: {
                'update:value': async (workInstructionVersionId, { flowEventEmitter }) => {
                    const {
                        argo_work_instruction_version_by_pk,
                    } = await gqlClient.request({
                        document: GetWorkInstructionVersionDetailDocument,
                        variables: {
                            workInstructionVersionId,
                        },
                    });
                    if (!argo_work_instruction_version_by_pk) {
                        Notify.error(`Work instruction version with ID ${workInstructionVersionId} not found.`);
                        return;
                    }
                    const configParseResult = workInstructionVersionSchema.safeParse(argo_work_instruction_version_by_pk.config);
                    if (!configParseResult.success) {
                        const zodError = fromZodError(configParseResult.error);
                        const err = CausalError.from({
                            message: 'Encountered an error while attempting to parse the config',
                            cause: zodError.message, // including the whole zod error causes too much noise, so we'll include it alongside it
                            zodError,
                        });
                        Notify.error(err);
                        return;
                    }

                    const workInstructionVersionConfig = configParseResult.data;

                    const processIds = workInstructionVersionConfig.steps.map(s => s.linkedProcessIds).flat();

                    if (!processIds.length) {
                        const newNodeCfg: NodeCfg<'workInstruction'> = {
                            ...nodeCfg,
                            controlValues: {
                                ...nodeCfg.controlValues,
                                workInstructionVersionId,
                                workInstructionDetail: {
                                    kind: 'default',
                                    workInstructionOutputs: workInstructionVersionConfig.outputs,
                                },
                            },
                        };
                        flowEventEmitter.emit('updateNodeCfg', {
                            newNodeCfg,
                        });
                        return;
                    }
                    const processDataElements = await gqlClient.request({
                        document: GetProcessDataElementsForWorkInstructionInputsDocument,
                        variables: {
                            processIds,
                        },
                    });

                    const steps: ProcessWorkInstruction['steps'] = [];

                    for (const step of workInstructionVersionConfig.steps) {
                        const stepProcessData: ProcessWorkInstruction['steps'][number] = {
                            processes: [],
                            name: step.name,
                        };
                        let remainingStepProcessIds = step.linkedProcessIds;
                        if (nodeCfg.controlValues.workInstructionDetail.kind === 'process') {
                            const existingStepProcessData = nodeCfg.controlValues.workInstructionDetail.steps.find(s => s.name === step.name);
                            if (existingStepProcessData) {
                                for (const existingProcess of existingStepProcessData.processes) {
                                    // if all of the following are true, reuse the existing process collection config to avoid making the user re-configure it
                                    // 1. Existing work instruction node has process related stuff
                                    // 2. The existing work instruction has a step with the same name as a step in the incoming work instruction
                                    // 3. The existing work instruction step has process related stuff
                                    // 4. The process in the step matches the process in the incoming step
                                    if (remainingStepProcessIds.includes(existingProcess.id)) {
                                        stepProcessData.processes.push(existingProcess);
                                        remainingStepProcessIds = remainingStepProcessIds.filter(i => i !== existingProcess.id);
                                    }
                                }
                            }
                        }
                        for (const processId of remainingStepProcessIds) {
                            const processData = processDataElements.process_process.filter(p => p.id === processId);
                            for (const process of processData) {
                                const processItem: ProcessWorkInstruction['steps'][number]['processes'][number] = {
                                    id: process.id,
                                    name: process.name,
                                    params: process.data_elements.filter(de => de.collection_kind === ProcessDataElementCollectionKindEnum.SpecParam).map(de => {
                                        if (de.kind === ProcessDataElementKindEnum.Boolean) {
                                            throw new Error('Process parameters cannot be booleans');
                                        } else if (de.kind === ProcessDataElementKindEnum.Material) {
                                            return {
                                                id: de.id,
                                                name: de.name,
                                                kind: 'string',
                                            };
                                        } else if (de.kind === ProcessDataElementKindEnum.Number) {
                                            return {
                                                id: de.id,
                                                name: de.name,
                                                kind: 'number',
                                            };
                                        } else if (de.kind === ProcessDataElementKindEnum.String) {
                                            return {
                                                id: de.id,
                                                name: de.name,
                                                kind: 'string',
                                            };
                                        } else if (de.kind === ProcessDataElementKindEnum.MaterialModel) {
                                            return {
                                                id: de.id,
                                                name: de.name,
                                                kind: 'string',
                                            };
                                        } else if (de.kind === ProcessDataElementKindEnum.MaterialClass) {
                                            return {
                                                id: de.id,
                                                name: de.name,
                                                kind: 'string',
                                            };
                                        } else {
                                            const u: never = de.kind;
                                            throw new Error(`Unknown data element kind: "${u}"`);
                                        }
                                    }),
                                    collectTrigger: {
                                        eventTrigger: '',
                                        evalTrigger: 'event',
                                    },
                                    dataElements: process.data_elements.filter(de => de.collection_kind === ProcessDataElementCollectionKindEnum.Collect).map(de => ({
                                        id: de.id,
                                        name: de.name,
                                        sourceVar: '',
                                    })),
                                };
                                stepProcessData.processes.push(processItem);
                            }
                        }
                        steps.push(stepProcessData);
                    }

                    const newNodeCfg: NodeCfg<'workInstruction'> = {
                        ...nodeCfg,
                        controlValues: {
                            ...nodeCfg.controlValues,
                            workInstructionVersionId,
                            location: nodeCfg.controlValues.location || {
                                id: '',
                            },
                            workInstructionDetail: {
                                steps,
                                kind: 'process',
                                workInstructionOutputs: workInstructionVersionConfig.outputs,
                            },
                        },
                    };

                    flowEventEmitter.emit('updateNodeCfg', {
                        newNodeCfg,
                    });
                },
            },
            value: nodeCfg.controlValues.workInstructionVersionId,
        }),
        location: defineControlOpts({ cfg: { kind: 'location' }, on: { 'update:value': defaultControlUpdateValueHandler }, value: nodeCfg.controlValues.location }),
    }),
    cfg: () => ({
        disabled: false,
        id: getUID(),
        kind: 'workInstruction',
        width: 240,
        controlValues: {
            workInstructionVersionId: '',
            workInstructionDetail: {
                kind: 'default',
                workInstructionOutputs: {},
            },
        },
    }),
    inputs: () => ({
        activate: { socket: 'event', label: 'Activate' },
        materialId: { socket: 'string', label: 'Material ID' },
    }),
    outputs: nodeCfg => {
        const outputs: Record<string, { socket: NodeSocketKind; label: string }> = {
            started: { socket: 'event', label: 'Started' },
            completed: { socket: 'event', label: 'Done' },
            stepUpdate: { socket: 'event', label: 'Step Update' },
            totalStepNum: { socket: 'number', label: 'Total Number of Steps' },
            currentStepNum: { socket: 'number', label: 'Current Step Number' },
            workInstructionName: { socket: 'string', label: 'Work Instruction Name' },
            workInstructionVersion: { socket: 'number', label: 'Work Instruction Version' },
        };
        for (const [ workInstructionOutputId, outputData ] of Object.entries(nodeCfg.controlValues.workInstructionDetail.workInstructionOutputs).sort(([ , v1 ], [ , v2 ]) => v1.idx < v2.idx ? -1 : 1)) {
            let nodeSocketKind: NodeSocketKind;
            if (typeof outputData.defaultValue === 'number') {
                nodeSocketKind = 'number';
            } else if (typeof outputData.defaultValue === 'boolean') {
                nodeSocketKind = 'boolean';
            } else if (typeof outputData.defaultValue === 'string') {
                nodeSocketKind = 'string';
            } else {
                throw new Error(`Unknown node socket kind: ${JSON.stringify(typeof outputData.defaultValue)}`);
            }
            outputs[workInstructionOutputId] = { socket: nodeSocketKind, label: outputData.name };
        }
        if (nodeCfg.controlValues.workInstructionDetail.kind === 'process') {
            for (const step of nodeCfg.controlValues.workInstructionDetail.steps) {
                for (const process of step.processes) {
                    for (const param of process.params) {
                        outputs[param.id] = { socket: param.kind, label: `${step.name} - ${process.name} - ${param.name}` };
                    }
                }
            }
        }
        return outputs as ReturnType<NodeSpec<NodeCfg<'workInstruction'>>['outputs']>;
    },
    validator: async node => {
        const errors: Record<string, string> = {};
        if (!node.controlValues.workInstructionVersionId) {
            errors.workInstructionVersionId = 'Work Instruction ID is required';
            // returning early to avoid running queries that will fail
            return errors;
        }

        const { argo_work_instruction_version_by_pk } = await gqlClient.request({
            document: GetWorkInstructionVersionDetailDocument,
            variables: { workInstructionVersionId: node.controlValues.workInstructionVersionId },
        });
        if (!argo_work_instruction_version_by_pk) {
            return `Work instruction version with ID ${node.controlValues.workInstructionVersionId} not found.`;
        }

        const workInstructionVersionStatus = argo_work_instruction_version_by_pk.status;
        if (workInstructionVersionStatus !== ArgoWorkInstructionVersionStatusEnum.Released) {
            if (workInstructionVersionStatus === ArgoWorkInstructionVersionStatusEnum.Retired) {
                if (!argo_work_instruction_version_by_pk.discontinue_after) {
                    errors.workInstructionVersionId = 'Work instruction version is retired and must have a discontinue_after date.';
                } else if (new Date() >= new Date(argo_work_instruction_version_by_pk.discontinue_after)) {
                    errors.workInstructionVersionId = 'Work instruction version is retired and must not be used after discontinue_after date.';
                }
            } else {
                errors.workInstructionVersionId = `Work instruction version is not released, current status: ${workInstructionVersionStatus}`;
            }
        }

        if (node.controlValues.workInstructionDetail.kind === 'process') {
            if (!node.controlValues.location ||
                ('deployParamKind' in node.controlValues.location && !node.controlValues.location.deployParamId) ||
                ('id' in node.controlValues.location && !node.controlValues.location.id)
            ) {
                errors.location = 'Location is required';
            }

            const allVariables = accessor.appletDesign.allAppletVariables;

            for (const step of node.controlValues.workInstructionDetail.steps) {
                for (const process of step.processes) {
                    for (const dataElement of process.dataElements) {
                        if (!dataElement.sourceVar) {
                            errors[`dataInput_${dataElement.id}`] = `Data input for ${step.name} - ${process.name} - ${dataElement.name} is required`;
                        } else if (!allVariables[dataElement.sourceVar]) {
                            errors[`dataInput_${dataElement.id}`] = `Data input for ${step.name} - ${process.name} - ${dataElement.name} is not found`;
                        }
                    }
                }
            }
        }

        return Object.keys(errors).length === 0 ? true : errors;
    },
};
